import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { formatCnpj } from "utils/federal-tax-id";

import { agruparPorRaiz } from "./alcance";
import { TEXTOS } from "./frases";
import {
  IAlcanceDoVinculo,
  IClienteDaCarteira,
  IUsuarioDaOrganizacao,
  PedidoDeAlcance,
} from "./gestao-de-usuarios.types";

const soDigitos = (s: string) => s.replace(/\D/g, "");
const semAcento = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const raizFormatada = (raiz: string) => raiz.replace(/^(\d{2})(\d{3})(\d{3})$/, "$1.$2.$3");

type Modo = "TODA" | "MARCADAS";

export function ModalDeAlcance(p: {
  aberto: boolean;
  ocupado: boolean;
  onFechar: () => void;
  usuario: Pick<IUsuarioDaOrganizacao, "nome">;
  atual: IAlcanceDoVinculo;
  carteira: IClienteDaCarteira[];
  onSalvar: (pedido: PedidoDeAlcance) => void;
}) {
  const { atual } = p;
  // why: decisão 2 do plano B: quem tem toda a carteira abre sem marcas; pendentes nascem sem marca.
  const inicialModo: Modo = atual.alcance === "SELECAO" ? "MARCADAS" : "TODA";
  const inicialIds = useMemo(() => (atual.alcance === "SELECAO" ? atual.incluidas : []), [atual]);
  const [modo, setModo] = useState<Modo>(inicialModo);
  const [marcadas, setMarcadas] = useState<string[]>(inicialIds);
  const [busca, setBusca] = useState("");
  const [confirmando, setConfirmando] = useState(false);

  const raizes = useMemo(
    () => agruparPorRaiz(p.carteira.filter((e) => atual.carteira.includes(e.organizationId))),
    [p.carteira, atual.carteira],
  );
  const total = raizes.reduce((n, r) => n + r.empresas.length, 0);
  const q = semAcento(busca.trim());
  const qDigitos = soDigitos(busca);
  const casa = (e: IClienteDaCarteira) =>
    !q || semAcento(e.nome).includes(q) || (qDigitos !== "" && soDigitos(e.cnpj).includes(qDigitos));

  const alternar = (ids: string[], marcar: boolean) =>
    setMarcadas((m) => (marcar ? [...new Set([...m, ...ids])] : m.filter((x) => !ids.includes(x))));

  // why: compara com o que está gravado, não com o radio inicial: GRUPO abre em "Toda a carteira" e ainda é salvável.
  const igual =
    modo === "TODA"
      ? atual.alcance === "ORGANIZACAO"
      : atual.alcance === "SELECAO" &&
        marcadas.length === atual.incluidas.length &&
        marcadas.every((id) => atual.incluidas.includes(id));
  const vazia = modo === "MARCADAS" && marcadas.length === 0;
  const nome = p.usuario.nome;

  return (
    <Dialog open={p.aberto} onClose={p.ocupado ? undefined : p.onFechar} fullWidth maxWidth="sm">
      <DialogTitle>{`Alcance de ${nome}`}</DialogTitle>
      <DialogContent>
        {confirmando ? (
          <Typography>{TEXTOS.CONFIRMA_SELECAO(marcadas.length, total, nome)}</Typography>
        ) : (
          <>
            <RadioGroup value={modo} onChange={(e) => setModo(e.target.value as Modo)}>
              <FormControlLabel value="TODA" control={<Radio />} label={TEXTOS.TODA_A_CARTEIRA} />
              <FormControlLabel value="MARCADAS" control={<Radio />} label={TEXTOS.EMPRESAS_MARCADAS} />
            </RadioGroup>
            {modo === "MARCADAS" && (
              <Box mt={1}>
                <TextField
                  label="Buscar por nome ou CNPJ"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  fullWidth
                  margin="dense"
                />
                <Typography variant="caption">{`${marcadas.length} de ${total} empresas marcadas`}</Typography>
                {raizes.map((r) => {
                  const visiveis = r.empresas.filter(casa);
                  if (visiveis.length === 0) return null;
                  const ids = r.empresas.map((e) => e.organizationId);
                  const n = ids.filter((id) => marcadas.includes(id)).length;
                  return (
                    <Box key={r.raiz} mt={1}>
                      <Typography variant="subtitle2">{`${r.nome} (${raizFormatada(r.raiz)})`}</Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={n === ids.length}
                            indeterminate={n > 0 && n < ids.length}
                            onChange={(e) => alternar(ids, e.target.checked)}
                            inputProps={{ "aria-label": `Marcar todas de ${r.nome}` }}
                          />
                        }
                        label="Marcar todas"
                      />
                      <Box pl={3}>
                        {visiveis.map((e) => (
                          <Box key={e.organizationId} display="flex" alignItems="center">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={marcadas.includes(e.organizationId)}
                                  onChange={(ev) => alternar([e.organizationId], ev.target.checked)}
                                />
                              }
                              label={`${e.nome} ${formatCnpj(e.cnpj)}`}
                            />
                            {atual.pendentes.includes(e.organizationId) && (
                              <Chip size="small" label={TEXTOS.PENDENTE} />
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
                {vazia && <FormHelperText>{TEXTOS.SELECAO_VAZIA}</FormHelperText>}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {confirmando ? (
          <>
            <Button onClick={() => setConfirmando(false)} disabled={p.ocupado}>
              Voltar
            </Button>
            <Button
              variant="contained"
              disabled={p.ocupado}
              onClick={() => p.onSalvar({ alcance: "SELECAO", organizacoes: marcadas })}
            >
              Confirmar
            </Button>
          </>
        ) : (
          <>
            <Button onClick={p.onFechar} disabled={p.ocupado}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={p.ocupado || igual || vazia}
              onClick={() => (modo === "TODA" ? p.onSalvar({ alcance: "ORGANIZACAO" }) : setConfirmando(true))}
            >
              Salvar
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
